import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { actionQueue, ActionType } from './ActionQueue';

const STORAGE_KEY = 'shopfloor-layout';

// Whether to track actions for undo/redo (can be toggled)
let trackActions = true;

const initialState = {
  // Assets
  assetsLoaded: false,
  manifest: null,
  
  // Layout
  layout: {
    version: 1,
    name: 'Untitled Layout',
    entities: [],
    gridSize: 30, // Default medium shop
    floorTiles: null, // null = full grid, Set of "x,y" strings = custom floor
  },
  
  // UI state
  selectedId: null,
  hoveredId: null,
  currentTool: 'select', // 'select' | 'place' | 'pan' | 'floor-draw' | 'floor-erase'
  selectedAssetKey: null,
  snapEnabled: true,
  showGrid: true,
  
  // Viewport (for status display)
  zoom: 1,
  
  // Machine status data (from WebSocket/API)
  machineStatus: {}, // { machineId: { state: 'RUNNING', kpis: { ... } } }
};

export const useShopStore = create((set, get) => ({
  ...initialState,
  
  // ========== Asset Actions ==========
  
  setManifest: (manifest) => set({ manifest, assetsLoaded: true }),
  
  // ========== Entity Actions ==========
  
  addEntity: (assetKey, tx, ty, options = {}) => {
    if (!assetKey) {
      console.warn('addEntity: No asset key provided');
      return null;
    }
    
    if (trackActions) {
      // Use action queue for undo support
      actionQueue.execute({
        type: ActionType.ADD_ENTITY,
        payload: { assetKey, tx, ty, options },
        store: { getState: get, setState: set },
      });
    } else {
      // Direct execution
      return get()._addEntityDirect(assetKey, tx, ty, options);
    }
  },
  
  updateEntityPos: (id, tx, ty) => {
    if (trackActions) {
      actionQueue.execute({
        type: ActionType.MOVE_ENTITY,
        payload: { id, tx, ty },
        store: { getState: get, setState: set },
      });
    } else {
      get()._updateEntityPosDirect(id, tx, ty);
    }
  },
  
  updateEntityMeta: (id, meta) => {
    if (trackActions) {
      actionQueue.execute({
        type: ActionType.UPDATE_ENTITY_META,
        payload: { id, meta },
        store: { getState: get, setState: set },
      });
    } else {
      get()._updateEntityMetaDirect(id, meta);
    }
  },
  
  rotateFacing: (id, direction = 'cw') => {
    if (trackActions) {
      actionQueue.execute({
        type: ActionType.ROTATE_ENTITY,
        payload: { id, direction },
        store: { getState: get, setState: set },
      });
    } else {
      get()._rotateFacingDirect(id, direction);
    }
  },
  
  markEntityDirty: (id) => {
    const { layout } = get();
    set({
      layout: {
        ...layout,
        entities: layout.entities.map((e) =>
          e.id === id ? { ...e, _dirty: true } : e
        ),
      },
    });
  },
  
  clearEntityDirty: (id) => {
    const { layout } = get();
    set({
      layout: {
        ...layout,
        entities: layout.entities.map((e) =>
          e.id === id ? { ...e, _dirty: false } : e
        ),
      },
    });
  },
  
  deleteEntity: (id) => {
    if (trackActions) {
      actionQueue.execute({
        type: ActionType.DELETE_ENTITY,
        payload: { id },
        store: { getState: get, setState: set },
      });
    } else {
      get()._deleteEntityDirect(id);
    }
  },
  
  getEntity: (id) => {
    const { layout } = get();
    return layout.entities.find((e) => e.id === id);
  },
  
  // ========== UI Actions ==========
  
  setSelected: (id) => set({ selectedId: id }),
  setHovered: (id) => set({ hoveredId: id }),
  setTool: (tool) => set({ currentTool: tool, selectedId: tool === 'place' ? null : get().selectedId }),
  setSelectedAsset: (key) => set({ selectedAssetKey: key, currentTool: key ? 'place' : 'select' }),
  toggleSnap: () => set({ snapEnabled: !get().snapEnabled }),
  toggleGrid: () => set({ showGrid: !get().showGrid }),
  setZoom: (zoom) => set({ zoom }),
  
  // ========== Floor/Grid Actions ==========
  
  setGridSize: (size) => {
    const { layout } = get();
    set({ layout: { ...layout, gridSize: size } });
  },
  
  enableCustomFloor: () => {
    const { layout } = get();
    if (layout.floorTiles !== null) return; // Already custom
    
    // Initialize with full grid as floor
    const tiles = new Set();
    for (let x = 0; x < layout.gridSize; x++) {
      for (let y = 0; y < layout.gridSize; y++) {
        tiles.add(`${x},${y}`);
      }
    }
    set({ layout: { ...layout, floorTiles: tiles } });
  },
  
  disableCustomFloor: () => {
    const { layout } = get();
    set({ layout: { ...layout, floorTiles: null }, currentTool: 'select' });
  },
  
  addFloorTile: (tx, ty) => {
    const { layout } = get();
    if (layout.floorTiles === null) return;
    
    const newTiles = new Set(layout.floorTiles);
    newTiles.add(`${tx},${ty}`);
    set({ layout: { ...layout, floorTiles: newTiles } });
  },
  
  removeFloorTile: (tx, ty) => {
    const { layout } = get();
    if (layout.floorTiles === null) return;
    
    const newTiles = new Set(layout.floorTiles);
    newTiles.delete(`${tx},${ty}`);
    set({ layout: { ...layout, floorTiles: newTiles } });
  },
  
  addFloorTiles: (tiles) => {
    const { layout } = get();
    if (layout.floorTiles === null) return;
    
    const newTiles = new Set(layout.floorTiles);
    tiles.forEach(({ tx, ty }) => newTiles.add(`${tx},${ty}`));
    set({ layout: { ...layout, floorTiles: newTiles } });
  },
  
  removeFloorTiles: (tiles) => {
    const { layout } = get();
    if (layout.floorTiles === null) return;
    
    const newTiles = new Set(layout.floorTiles);
    tiles.forEach(({ tx, ty }) => newTiles.delete(`${tx},${ty}`));
    set({ layout: { ...layout, floorTiles: newTiles } });
  },
  
  isFloorTile: (tx, ty) => {
    const { layout } = get();
    if (layout.floorTiles === null) return true; // Full grid mode
    return layout.floorTiles.has(`${tx},${ty}`);
  },
  
  clearFloorTiles: () => {
    const { layout } = get();
    if (layout.floorTiles === null) return;
    set({ layout: { ...layout, floorTiles: new Set() } });
  },
  
  fillFloorTiles: () => {
    const { layout } = get();
    const tiles = new Set();
    for (let x = 0; x < layout.gridSize; x++) {
      for (let y = 0; y < layout.gridSize; y++) {
        tiles.add(`${x},${y}`);
      }
    }
    set({ layout: { ...layout, floorTiles: tiles } });
  },
  
  // ========== Layout Actions ==========
  
  setLayoutName: (name) => {
    const { layout } = get();
    set({ layout: { ...layout, name } });
  },
  
  clearLayout: () => {
    if (!confirm('Clear all entities from the layout?')) return;
    
    if (trackActions) {
      actionQueue.execute({
        type: ActionType.CLEAR_LAYOUT,
        payload: {},
        store: { getState: get, setState: set },
      });
    } else {
      get()._clearLayoutDirect();
    }
    
    // Also clear from localStorage so they don't come back on refresh
    localStorage.removeItem(STORAGE_KEY);
  },
  
  saveLayout: () => {
    const { layout } = get();
    // Convert Set to array for JSON serialization
    const serializable = {
      ...layout,
      floorTiles: layout.floorTiles ? Array.from(layout.floorTiles) : null,
    };
    const json = JSON.stringify(serializable, null, 2);
    localStorage.setItem(STORAGE_KEY, json);
    return json;
  },
  
  loadLayout: (json) => {
    try {
      const parsed = typeof json === 'string' ? JSON.parse(json) : json;
      if (!parsed.entities) {
        throw new Error('Invalid layout format');
      }
      // Convert array back to Set
      const layout = {
        ...parsed,
        gridSize: parsed.gridSize || 30,
        floorTiles: parsed.floorTiles ? new Set(parsed.floorTiles) : null,
      };
      set({ layout, selectedId: null });
      return true;
    } catch (err) {
      console.error('Failed to load layout:', err);
      return false;
    }
  },
  
  loadFromStorage: () => {
    const json = localStorage.getItem(STORAGE_KEY);
    if (json) {
      return get().loadLayout(json);
    }
    return false;
  },
  
  exportLayout: () => {
    const { layout } = get();
    const json = JSON.stringify(layout, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${layout.name.replace(/[^a-z0-9]/gi, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
  
  // ========== Machine Status Actions ==========
  
  updateMachineStatus: (machineId, status) => {
    set({
      machineStatus: {
        ...get().machineStatus,
        [machineId]: status,
      },
    });
  },
  
  setAllMachineStatus: (statusMap) => {
    set({ machineStatus: statusMap });
  },
  
  getMachineStatus: (machineId) => {
    return get().machineStatus[machineId] || null;
  },
  
  // ========== Internal "Direct" Methods (for ActionQueue) ==========
  // These bypass the action queue and are used for execute/undo operations
  
  _addEntityDirect: (assetKey, tx, ty, options = {}) => {
    const { layout } = get();
    
    if (!assetKey) {
      console.warn('_addEntityDirect: No asset key provided');
      return null;
    }
    
    const id = uuidv4();
    const entity = {
      id,
      assetKey,
      tx: Math.round(tx),
      ty: Math.round(ty),
      facing: options.facing || 'NE',
      meta: options.meta || {},
      _dirty: true,
      _pixiContainer: null,
    };
    
    set({
      layout: {
        ...layout,
        entities: [...layout.entities, entity],
      },
      selectedId: id,
    });
    
    return id;
  },
  
  _deleteEntityDirect: (id) => {
    const { layout, selectedId } = get();
    set({
      layout: {
        ...layout,
        entities: layout.entities.filter((e) => e.id !== id),
      },
      selectedId: selectedId === id ? null : selectedId,
    });
  },
  
  _updateEntityPosDirect: (id, tx, ty) => {
    const { layout } = get();
    set({
      layout: {
        ...layout,
        entities: layout.entities.map((e) =>
          e.id === id ? { ...e, tx, ty, _dirty: true } : e
        ),
      },
    });
  },
  
  _updateEntityMetaDirect: (id, meta) => {
    const { layout } = get();
    set({
      layout: {
        ...layout,
        entities: layout.entities.map((e) =>
          e.id === id ? { ...e, meta: { ...e.meta, ...meta }, _dirty: true } : e
        ),
      },
    });
  },
  
  _setEntityMeta: (id, meta) => {
    const { layout } = get();
    set({
      layout: {
        ...layout,
        entities: layout.entities.map((e) =>
          e.id === id ? { ...e, meta, _dirty: true } : e
        ),
      },
    });
  },
  
  _rotateFacingDirect: (id, direction = 'cw') => {
    const DIRECTIONS = ['NE', 'SE', 'SW', 'NW'];
    const { layout } = get();
    set({
      layout: {
        ...layout,
        entities: layout.entities.map((e) => {
          if (e.id !== id) return e;
          const currentIdx = DIRECTIONS.indexOf(e.facing);
          const newIdx = direction === 'cw' 
            ? (currentIdx + 1) % 4 
            : (currentIdx + 3) % 4;
          return { ...e, facing: DIRECTIONS[newIdx], _dirty: true };
        }),
      },
    });
  },
  
  _clearLayoutDirect: () => {
    set({
      layout: { ...get().layout, entities: [] },
      selectedId: null,
    });
  },
  
  _restoreEntity: (entity) => {
    const { layout } = get();
    set({
      layout: {
        ...layout,
        entities: [...layout.entities, { ...entity, _dirty: true }],
      },
    });
  },
  
  _restoreEntities: (entities) => {
    const { layout } = get();
    set({
      layout: {
        ...layout,
        entities: entities.map(e => ({ ...e, _dirty: true })),
      },
    });
  },
  
  // ========== Action Queue Integration ==========
  
  setTrackActions: (enabled) => {
    trackActions = enabled;
  },
  
  undo: () => {
    actionQueue.undo();
  },
  
  redo: () => {
    actionQueue.redo();
  },
  
  canUndo: () => actionQueue.canUndo(),
  canRedo: () => actionQueue.canRedo(),
}));

// Initialize action queue with store reference
// Note: Actions are tracked automatically when using the public methods
